import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import SEO from "../components/SEO";
import carpetSpinningImg from "../assets/images/carpet_wool_spinning.jpg";
import doorwayBalesImg from "../assets/images/authentic/warehouse_doorway_bales.webp";
import {
  Sparkles,
  Award,
  Sliders,
  ArrowRight,
  Phone,
  MessageCircle,
  Factory,
  Layers,
  Ruler,
  Flame,
} from "lucide-react";

const CarpetWoolSupplierIndia = () => {
  const { lPath, isRTL } = useLanguage();

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://skwoolens.trivanahospitality.com/en"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Carpet Wool Supplier in India",
        "item": "https://skwoolens.trivanahospitality.com/en/carpet-wool-supplier-india"
      }
    ]
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Carpet-Grade Wool Supply in India",
    "provider": {
      "@type": "WholesaleStore",
      "name": "S.K. Woollens",
      "telephone": "+91-7014581250",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "B-26, Un Mandi, Antyodaya Nagar",
        "addressLocality": "Bikaner",
        "addressRegion": "Rajasthan",
        "postalCode": "334001",
        "addressCountry": "IN"
      }
    },
    "description": "Specialist supplier of carpet-grade raw wool (30+ and 32-34+ micron) for carpet yarn spinners in Bhadohi, Panipat, Mirzapur, Jaipur, and nationwide.",
    "areaServed": "India"
  };

  const schema = [breadcrumbSchema, serviceSchema];

  return (
    <div className="space-y-16 sm:space-y-24 py-6">
      <SEO
        title="Carpet Wool Supplier in India | 30+ & 32-34+ Micron | S.K. Woollens"
        description="Premier carpet wool supplier in India. S.K. Woollens supplies high-crimp 30+ & 32-34+ micron carpet-grade raw wool to yarn spinners in Bhadohi, Mirzapur & Panipat."
        keywords="carpet wool supplier in India, carpet-grade wool supplier in India, wool for carpet manufacturers, 30 micron wool supplier, 32 micron wool, carpet yarn wool India"
        canonicalUrl="https://skwoolens.trivanahospitality.com/en/carpet-wool-supplier-india"
        schema={schema}
      />

      {/* 1. Header Banner */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-gradient-to-br from-wool-sand/50 via-white to-wool-sand/30 border border-wool-border rounded-3xl p-8 sm:p-14 shadow-subtle">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7 space-y-4">
              <div className="wool-badge inline-flex">
                <Sparkles className="w-3.5 h-3.5 text-wool-bronze" />
                <span>30+ Micron & 32–34+ Micron Focus</span>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-wool-charcoal tracking-tight font-display">
                Carpet Wool Supplier in India
              </h1>
              <p className="text-base sm:text-lg text-wool-slate leading-relaxed">
                Specialized raw wool sourcing for carpet yarn spinning mills. We supply resilient, high-crimp coarse fibres engineered for luxurious pile recovery, exceptional wear durability, and smooth carding performance.
              </p>

              <div className="p-4 bg-white/80 border border-wool-border rounded-xl text-xs sm:text-sm text-wool-slate">
                <span className="font-semibold text-wool-bronze block mb-1">
                  Trusted by Carpet Spinning Hubs:
                </span>
                Supplying dedicated lots to carpet yarn spinning facilities across Bhadohi, Mirzapur, Panipat, Jaipur, and Bikaner since 1965.
              </div>

              <div className="pt-2 flex flex-wrap items-center gap-3">
                <a
                  href="https://wa.me/917014581250?text=Hi%2C%20I%20am%20looking%20for%20carpet-grade%20wool%20for%20carpet%20yarn%20spinning"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary inline-flex items-center gap-2"
                >
                  <MessageCircle className="w-4 h-4 text-emerald-300" />
                  <span>Enquire for Carpet Wool</span>
                  <ArrowRight className={`w-4 h-4 ${isRTL ? "rotate-180" : ""}`} />
                </a>

                <a href="tel:+917014581250" className="btn-secondary flex items-center gap-2">
                  <Phone className="w-4 h-4 text-wool-bronze" />
                  <span>Call +91 7014581250</span>
                </a>
              </div>
            </div>

            <div className="lg:col-span-5 relative">
              <div className="relative rounded-2xl overflow-hidden shadow-xl border border-wool-border bg-white">
                <img
                  src={carpetSpinningImg}
                  alt="Carpet Yarn Spinning with Indian Coarse Raw Wool"
                  className="w-full h-72 sm:h-80 object-cover"
                  loading="eager"
                />
                <div className="p-3 bg-wool-sand/80 border-t border-wool-border text-center text-xs font-semibold text-wool-charcoal">
                  High-Bulk Raw Wool Graded for Carpet Yarn Spinning
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Technical Requirements for Carpet Yarn Wool */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white border border-wool-border rounded-3xl p-6 sm:p-10 shadow-subtle space-y-6">
          <div className="max-w-3xl space-y-3">
            <div className="wool-badge">
              <Sliders className="w-3.5 h-3.5 text-wool-bronze" />
              <span>Technical Spinning Parameters</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-wool-charcoal font-display">
              Why Micron, Crimp & Resilience Matter in Carpet Wool
            </h2>
            <p className="text-sm sm:text-base text-wool-slate leading-relaxed">
              Unlike apparel-grade fine wool, high-performance carpet yarns demand robust fibre diameter and superior mechanical spring-back. If the wool is too fine, carpet pile mats down under heavy foot traffic. S.K. Woollens evaluates every batch specifically for carpet manufacturing criteria:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4">
            <div className="wool-card p-6 space-y-3 bg-wool-sand/20">
              <Flame className="w-6 h-6 text-wool-bronze" />
              <h3 className="text-base font-bold text-wool-charcoal">Fibre Resilience & Crimp</h3>
              <p className="text-xs text-wool-slate leading-relaxed">
                Indigenous Bikaner and regional Indian carpet wools feature strong natural helical crimp. This gives tufted and hand-knotted carpets superior springiness and resistance to crushing.
              </p>
            </div>

            <div className="wool-card p-6 space-y-3 bg-wool-sand/20">
              <Ruler className="w-6 h-6 text-wool-bronze" />
              <h3 className="text-base font-bold text-wool-charcoal">Optimal Carding Staple Length</h3>
              <p className="text-xs text-wool-slate leading-relaxed">
                We supply lots with consistent 50mm–100mm+ staple lengths that feed smoothly through industrial woolen carding engines without excessive fibre breakage or fly waste.
              </p>
            </div>

            <div className="wool-card p-6 space-y-3 bg-wool-sand/20">
              <Layers className="w-6 h-6 text-wool-bronze" />
              <h3 className="text-base font-bold text-wool-charcoal">Low Vegetable Matter (VM)</h3>
              <p className="text-xs text-wool-slate leading-relaxed">
                Vegetable burrs can cause yarn breakages and specks in finished carpets. Our grading process strictly limits VM to protect your spinning line efficiency.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Micron Grades Specifically Handled */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto space-y-3 mb-10">
          <div className="wool-badge">
            <Award className="w-3.5 h-3.5 text-wool-bronze" />
            <span>Micron Classification</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-wool-charcoal font-display">
            Carpet Wool Micron Grades Available
          </h2>
          <p className="text-sm sm:text-base text-wool-muted">
            Specification-based procurement matching your specific yarn count (e.g. 2/20s, 3/60s, or coarse counts).
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="bg-white border border-wool-border rounded-2xl p-6 shadow-sm space-y-3">
            <span className="text-[11px] font-bold text-wool-bronze uppercase">Grade Selection A</span>
            <h3 className="text-lg font-bold text-wool-charcoal">30+ Micron Coarse Fleece</h3>
            <p className="text-xs text-wool-slate leading-relaxed">
              Provides the ideal balance between spinning draft and yarn body. Frequently blended with finer wools to impart necessary structural firmness and crisp hand-feel.
            </p>
            <ul className="text-xs text-wool-muted space-y-1 pt-1">
              <li>• Suitable for Hand-Tufted & Woven Rugs</li>
              <li>• Natural White & Cream Tones</li>
              <li>• Clean Yield Tested</li>
            </ul>
          </div>

          <div className="bg-white border border-wool-border rounded-2xl p-6 shadow-sm space-y-3">
            <span className="text-[11px] font-bold text-wool-bronze uppercase">Grade Selection B</span>
            <h3 className="text-lg font-bold text-wool-charcoal">32–34+ Micron Extra Coarse</h3>
            <p className="text-xs text-wool-slate leading-relaxed">
              High-tensile heavy-duty carpet wool suited for maximum resilience, contract commercial carpets, hospitality flooring, and thick Berber-style yarns.
            </p>
            <ul className="text-xs text-wool-muted space-y-1 pt-1">
              <li>• Maximum Pile Recovery & Bounce</li>
              <li>• Resists Crushing in High Foot Traffic</li>
              <li>• Available Greasy or Scoured</li>
            </ul>
          </div>

          <div className="bg-white border border-wool-border rounded-2xl p-6 shadow-sm space-y-3">
            <span className="text-[11px] font-bold text-wool-bronze uppercase">Grade Selection C</span>
            <h3 className="text-lg font-bold text-wool-charcoal">Custom Blend Lot Matching</h3>
            <p className="text-xs text-wool-slate leading-relaxed">
              We source and blend domestic clips to match your exact spinning sheet parameters, including targeted colour sorting for natural undyed carpet collections.
            </p>
            <ul className="text-xs text-wool-muted space-y-1 pt-1">
              <li>• Formulated to Mill Technical Sheets</li>
              <li>• Controlled Colour Shading</li>
              <li>• Multi-Tonne Lot Consistency</li>
            </ul>
          </div>
        </div>
      </section>

      {/* 4. Warehouse & Dispatch Facility */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center bg-wool-charcoal text-white rounded-3xl p-6 sm:p-12 shadow-2xl">
          <div className="lg:col-span-6 space-y-4">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/10 text-wool-sand text-xs font-bold uppercase tracking-wider rounded-full">
              <Factory className="w-3.5 h-3.5 text-wool-sand" />
              <span>Dedicated Supply to Carpet Hubs</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight font-display">
              Serving Spinners in Bhadohi, Panipat & Mirzapur
            </h2>
            <p className="text-xs sm:text-sm text-wool-sand/80 leading-relaxed">
              Carpet yarn manufacturers require uninterrupted supply of consistent fleece to keep spinning frames running efficiently. S.K. Woollens provides scheduled dispatches directly from our Bikaner godowns to major carpet clusters, backed by transparent lot testing and certified weights.
            </p>
            <div className="p-4 bg-white/5 border border-white/10 rounded-xl text-xs text-wool-sand/80">
              <span className="text-white font-semibold block mb-0.5">Prompt Sample Verification:</span>
              Request representative fleece samples before booking full truckload consignments.
            </div>
          </div>

          <div className="lg:col-span-6 relative rounded-2xl overflow-hidden border border-white/20">
            <img
              src={doorwayBalesImg}
              alt="Carpet Wool Bales Stored at S.K. Woollens Bikaner Godown"
              className="w-full h-72 sm:h-80 object-cover"
              loading="lazy"
            />
            <div className="p-3 bg-black/60 backdrop-blur-md border-t border-white/20 text-center text-xs text-wool-sand">
              Pressed Carpet Wool Bales Ready for Dispatch
            </div>
          </div>
        </div>
      </section>

      {/* 5. Related Pages Navigation */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="border border-wool-border bg-white rounded-3xl p-6 sm:p-10 shadow-subtle space-y-6">
          <h3 className="text-xl font-bold text-wool-charcoal font-display">
            Explore Related Wool Capabilities
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link
              to={lPath("/wool-supplier-bikaner")}
              className="p-4 border border-wool-border rounded-xl hover:border-wool-bronze hover:bg-wool-sand/20 transition flex flex-col justify-between"
            >
              <div>
                <h4 className="text-sm font-bold text-wool-charcoal">Wool Supplier in Bikaner</h4>
                <p className="text-xs text-wool-muted mt-1">Direct mandi gate procurement and storage at Un Mandi B-26.</p>
              </div>
              <span className="text-xs font-semibold text-wool-bronze mt-3 inline-flex items-center gap-1">
                Read more <ArrowRight className="w-3 h-3" />
              </span>
            </Link>

            <Link
              to={lPath("/raw-wool-supplier-india")}
              className="p-4 border border-wool-border rounded-xl hover:border-wool-bronze hover:bg-wool-sand/20 transition flex flex-col justify-between"
            >
              <div>
                <h4 className="text-sm font-bold text-wool-charcoal">Raw Wool Supplier India</h4>
                <p className="text-xs text-wool-muted mt-1">Domestic wool sourcing and nationwide bulk delivery networks.</p>
              </div>
              <span className="text-xs font-semibold text-wool-bronze mt-3 inline-flex items-center gap-1">
                Read more <ArrowRight className="w-3 h-3" />
              </span>
            </Link>

            <Link
              to={lPath("/wool-specifications")}
              className="p-4 border border-wool-border rounded-xl hover:border-wool-bronze hover:bg-wool-sand/20 transition flex flex-col justify-between"
            >
              <div>
                <h4 className="text-sm font-bold text-wool-charcoal">Wool Specifications Matrix</h4>
                <p className="text-xs text-wool-muted mt-1">Detailed breakdown of micron, staple length, yield, and VM.</p>
              </div>
              <span className="text-xs font-semibold text-wool-bronze mt-3 inline-flex items-center gap-1">
                Read more <ArrowRight className="w-3 h-3" />
              </span>
            </Link>

            <Link
              to={lPath("/bulk-wool-supply")}
              className="p-4 border border-wool-border rounded-xl hover:border-wool-bronze hover:bg-wool-sand/20 transition flex flex-col justify-between"
            >
              <div>
                <h4 className="text-sm font-bold text-wool-charcoal">Bulk Wool Supply</h4>
                <p className="text-xs text-wool-muted mt-1">Multi-tonne mill supply contracts and scheduled deliveries.</p>
              </div>
              <span className="text-xs font-semibold text-wool-bronze mt-3 inline-flex items-center gap-1">
                Read more <ArrowRight className="w-3 h-3" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* 6. Direct Contact CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-gradient-to-r from-wool-sand via-wool-sand-dark to-wool-sand border border-wool-border rounded-3xl p-8 sm:p-12 text-center space-y-6 shadow-premium">
          <div className="max-w-2xl mx-auto space-y-2">
            <h3 className="text-2xl sm:text-3xl font-extrabold text-wool-charcoal font-display">
              Discuss Your Carpet Wool Requirements
            </h3>
            <p className="text-xs sm:text-sm text-wool-slate">
              Contact our partners directly to request samples, confirm lot availability, or discuss monthly spinning contracts.
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-4">
            <a
              href="https://wa.me/917014581250?text=Hi%2C%20I%20would%20like%20to%20enquire%20about%20carpet%20wool%20supply%20(30%2B%20and%2032-34%2B%20micron)"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center gap-2"
            >
              <MessageCircle className="w-4 h-4 text-emerald-300" />
              <span>WhatsApp Carpet Wool Desk</span>
            </a>

            <a
              href="tel:+917014581250"
              className="btn-secondary flex items-center gap-2 bg-white"
            >
              <Phone className="w-4 h-4 text-wool-bronze" />
              <span>Call +91 7014581250</span>
            </a>

            <Link to={lPath("/contact")} className="btn-secondary bg-white">
              <span>Contact S.K. Woollens</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CarpetWoolSupplierIndia;
